import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || (slug === 'all' ? '全部' : slug) || '全部';

  const filteredComics = slug === 'all'
    ? comics
    : comics.filter((c) => c.categorySlug === slug);

  const schemaBreadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('breadcrumb.home'),
      item: 'http://jisutxt.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: t('nav.category'),
      item: 'http://jisutxt.com/category/all',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: categoryName,
      item: `http://jisutxt.com/category/${slug}`,
    },
  ];

  const schemaComicList = filteredComics.slice(0, 6).map((comic, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: comic.title,
    url: `http://jisutxt.com/comic/${comic.slug}`,
  }));

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${categoryName}漫画 - 八仔漫画网`,
          description: `八仔漫画网${categoryName}漫画分类，提供${categoryName}类漫画免费在线浏览。汇集热门${categoryName}漫画资源，每日更新，纯净无广告阅读。`,
          url: `http://jisutxt.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '八仔漫画网',
            url: 'http://jisutxt.com/',
          },
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: schemaComicList,
          },
        }}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: schemaBreadcrumbItems,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('nav.category'), href: '/category/all' },
                { label: categoryName },
              ]}
            />

            <section className="w-full px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-6xl mx-auto">
                <span className="inline-block text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full mb-4">
                  {categoryName}漫画
                </span>
                <h1 className="text-3xl md:text-5xl font-heading font-light text-foreground-900 leading-tight mb-6">
                  探索{categoryName}
                  <br />
                  漫画世界
                </h1>
                <p className="text-sm text-foreground-500 max-w-lg mb-6">
                  共找到 {filteredComics.length} 部{categoryName}漫画作品，每日更新，纯净阅读体验
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  {slug !== 'all' && (
                    <Link
                      to="/category/all"
                      className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium border border-foreground-300 text-foreground-700 hover:bg-foreground-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t('category.all')}
                    </Link>
                  )}
                  <Link
                    to={`/comic/${filteredComics[0]?.slug || 'quanzhi-fashi'}`}
                    className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('home.readNow')}
                  </Link>
                </div>
              </div>
            </section>
          </div>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-light text-foreground-900 mb-2">
                {categoryName}漫画列表
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                共 {filteredComics.length} 部作品
              </p>

              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      slug={comic.slug}
                      title={comic.title}
                      cover={comic.cover}
                      category={comic.category}
                      status={comic.status as 'ongoing' | 'completed'}
                      rating={comic.rating}
                      chaptersCount={comic.chapters.length}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无该类别的漫画</p>
                </div>
              )}
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}